import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Alert, Box, Stack, Typography } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { alpha } from "@mui/material/styles";
import { useErrorDialog } from "../../../contexts/ErrorDialogContext";
import SingpassCorppassLogin from "./SingpassCorppassLogin";
import {
  clearIrasOAuthInFlight,
  persistIrasOAuthReturnContext,
  redirectToIrasAuthorization,
} from "./irasOAuthHelpers";

export const useIrasAuthRedirect = ({
  company,
  getInitialAuth,
  logLabel,
  fallbackErrorMessage,
}) => {
  const handleError = useErrorDialog();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [authStatus, setAuthStatus] = useState("disconnected");

  const handleAuth = async () => {
    try {
      setIsLoading(true);
      setAuthStatus("connecting");

      const uen = company?.uen ? `${company.uen}`.trim() : "";
      if (!uen) {
        throw new Error("Company UEN is required to connect to IRAS.");
      }

      persistIrasOAuthReturnContext(company, location);
      const response = await getInitialAuth(uen);
      redirectToIrasAuthorization(response);
    } catch (error) {
      clearIrasOAuthInFlight();
      console.error(`${logLabel} failed:`, error);
      setAuthStatus("error");
      handleError(
        error?.message || error?.error?.message || fallbackErrorMessage
      );
      setIsLoading(false);
    }
  };

  return { authStatus, handleAuth, isLoading };
};

export const CompactIrasAuthCard = ({
  icon,
  iconColor = "primary",
  title,
  description,
  connectingMessage,
  errorMessage,
  footerText,
  loginProps,
}) => {
  const { authStatus, ...buttonProps } = loginProps;

  return (
    <Box
      sx={(theme) => ({
        width: "100%",
        height: "100%",
        p: 2,
        borderRadius: 1.5,
        bgcolor: alpha(theme.palette.primary.main, 0.035),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
      })}
    >
      <Stack spacing={1.75} sx={{ height: "100%" }}>
        <Stack direction='row' spacing={1.25} alignItems='center'>
          <Box
            sx={(theme) => ({
              width: 38,
              height: 38,
              flex: "0 0 38px",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette[iconColor].main, 0.1),
              color: theme.palette[iconColor].main,
            })}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {description}
            </Typography>
          </Box>
        </Stack>

        {authStatus === "connecting" && (
          <Alert severity='info' variant='outlined'>
            {connectingMessage}
          </Alert>
        )}

        {authStatus === "error" && (
          <Alert severity='error' variant='outlined'>
            {errorMessage}
          </Alert>
        )}

        <SingpassCorppassLogin {...buttonProps} />

        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            p: 1.25,
            borderRadius: 1.25,
            bgcolor: alpha(theme.palette.success.main, 0.045),
            border: `1px solid ${alpha(theme.palette.success.main, 0.16)}`,
          })}
        >
          <Box
            sx={(theme) => ({
              width: 24,
              height: 24,
              flex: "0 0 24px",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.success.main, 0.1),
              color: theme.palette.success.main,
            })}
          >
            <LockIcon sx={{ fontSize: 15 }} />
          </Box>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ lineHeight: 2 }}
          >
            {footerText}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};
